import React from 'react';
import { Link } from 'react-router-dom';
import { Plane, Shield, TrendingUp, Clock, AlertTriangle, CheckCircle2, ArrowRight } from 'lucide-react';

const LandingPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1A2B4A] via-[#243552] to-[#1A2B4A]">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-[#1A2B4A]/95 backdrop-blur-sm border-b border-[#4A90E2]/20 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <Plane className="h-8 w-8 text-[#4A90E2]" />
              <span className="text-2xl font-heading font-bold text-white">Winglet</span>
            </div>
            <div className="hidden md:flex space-x-8">
              <Link to="/how-it-works" className="text-gray-300 hover:text-[#4A90E2] transition-colors">How It Works</Link>
              <Link to="/pricing" className="text-gray-300 hover:text-[#4A90E2] transition-colors">Pricing</Link>
              <Link to="/compare-tools" className="text-gray-300 hover:text-[#4A90E2] transition-colors">Compare</Link>
              <Link to="/about" className="text-gray-300 hover:text-[#4A90E2] transition-colors">About</Link>
              <Link to="/data-privacy" className="text-gray-300 hover:text-[#4A90E2] transition-colors">Data Privacy</Link>
              <Link to="/aviation-hub" className="text-gray-300 hover:text-[#4A90E2] transition-colors">Aviation Hub</Link>
            </div>
            <div className="flex space-x-4">
              <Link to="/login" className="text-gray-300 hover:text-white transition-colors">Sign In</Link>
              <Link to="/signup" className="bg-[#4A90E2] hover:bg-[#3A7BC8] text-white px-6 py-2 rounded-lg transition-colors">
                Start Free Trial
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center">
            <div className="inline-block mb-4">
              <span className="bg-[#4A90E2]/20 text-[#4A90E2] px-4 py-2 rounded-full text-sm font-semibold">
                ✈️ The AI-Powered Co-Pilot for Your Career
              </span>
            </div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-heading font-bold text-white mb-6 leading-tight">
              Stop Flying Blind.<br />
              <span className="text-[#4A90E2]">Start Flying Smart.</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-8 max-w-3xl mx-auto leading-relaxed">
              The only crew scheduling app that catches company pay errors,
              predicts FAR 117 violations, and protects your PWA rights.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <Link 
                to="/signup" 
                className="bg-[#4A90E2] hover:bg-[#3A7BC8] text-white px-8 py-4 rounded-lg text-lg font-semibold transition-all transform hover:scale-105 inline-flex items-center justify-center group"
              >
                Try Free for 1.5 Months - No Credit Card
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <a 
                href="#demo-video" 
                className="border-2 border-[#4A90E2] text-[#4A90E2] hover:bg-[#4A90E2] hover:text-white px-8 py-4 rounded-lg text-lg font-semibold transition-all"
              >
                Watch Demo (90 sec)
              </a>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto text-left">
              <div className="flex items-start space-x-2">
                <CheckCircle2 className="h-6 w-6 text-[#4A90E2] flex-shrink-0 mt-1" />
                <span className="text-gray-300">Reroute Protection (Section 23L)</span>
              </div>
              <div className="flex items-start space-x-2">
                <CheckCircle2 className="h-6 w-6 text-[#4A90E2] flex-shrink-0 mt-1" />
                <span className="text-gray-300">Pay Variance Detection</span>
              </div>
              <div className="flex items-start space-x-2">
                <CheckCircle2 className="h-6 w-6 text-[#4A90E2] flex-shrink-0 mt-1" />
                <span className="text-gray-300">FAR 117 Compliance</span>
              </div>
              <div className="flex items-start space-x-2">
                <CheckCircle2 className="h-6 w-6 text-[#4A90E2] flex-shrink-0 mt-1" />
                <span className="text-gray-300">Real-Time Tracking</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Statement */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#0F1824]">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-heading font-bold text-white text-center mb-4">
            You Shouldn't Have to Choose
          </h2>
          <p className="text-xl text-gray-400 text-center mb-16 max-w-3xl mx-auto">
            Between accurate pay, FAR 117 protection, and flight tracking. Winglet gives you all three.
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            {/* Problem 1 */}
            <div className="bg-[#1A2B4A] border border-red-500/30 rounded-xl p-8 hover:border-red-500/50 transition-colors">
              <div className="w-12 h-12 bg-red-500/20 rounded-lg flex items-center justify-center mb-4">
                <AlertTriangle className="h-6 w-6 text-red-400" />
              </div>
              <h3 className="text-xl font-heading font-bold text-white mb-3">
                ❌ Company Gets Your Pay Wrong
              </h3>
              <p className="text-gray-400 mb-4">
                Payroll errors happen. Most pilots never catch them.
              </p>
              <div className="bg-[#4A90E2]/10 border border-[#4A90E2]/30 rounded-lg p-4">
                <p className="text-[#4A90E2] font-semibold">✓ Winglet catches it automatically</p>
                <p className="text-gray-400 text-sm mt-1">Line-by-line variance detection with audit trail</p>
              </div>
            </div>

            {/* Problem 2 */}
            <div className="bg-[#1A2B4A] border border-amber-500/30 rounded-xl p-8 hover:border-amber-500/50 transition-colors">
              <div className="w-12 h-12 bg-amber-500/20 rounded-lg flex items-center justify-center mb-4">
                <Shield className="h-6 w-6 text-amber-400" />
              </div>
              <h3 className="text-xl font-heading font-bold text-white mb-3">
                ❌ Reroutes Are Confusing
              </h3>
              <p className="text-gray-400 mb-4">
                No pay calculator. No FAR 117 check. You're guessing if it's legal and what premium you're owed.
              </p>
              <div className="bg-[#4A90E2]/10 border border-[#4A90E2]/30 rounded-lg p-4">
                <p className="text-[#4A90E2] font-semibold">✓ Winglet shows RED/YELLOW/GREEN alerts</p>
                <p className="text-gray-400 text-sm mt-1">Section 23L calculator + refusal letter generator</p>
              </div>
            </div>

            {/* Problem 3 */}
            <div className="bg-[#1A2B4A] border border-red-500/30 rounded-xl p-8 hover:border-red-500/50 transition-colors">
              <div className="w-12 h-12 bg-red-500/20 rounded-lg flex items-center justify-center mb-4">
                <Clock className="h-6 w-6 text-red-400" />
              </div>
              <h3 className="text-xl font-heading font-bold text-white mb-3">
                ❌ FAR 117 Violations Surprise You
              </h3>
              <p className="text-gray-400 mb-4">
                Duty limits calculated <span className="text-white font-semibold">after</span> you accept the trip. 
                Too late to refuse safely.
              </p>
              <div className="bg-[#4A90E2]/10 border border-[#4A90E2]/30 rounded-lg p-4">
                <p className="text-[#4A90E2] font-semibold">✓ Winglet predicts violations before you accept</p>
                <p className="text-gray-400 text-sm mt-1">Proactive compliance checking with rolling limits</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="bg-gradient-to-br from-[#4A90E2]/20 to-[#1A2B4A] border border-[#4A90E2]/30 rounded-2xl p-12">
            <h2 className="text-4xl font-heading font-bold text-white text-center mb-6">
              One App. Complete Protection.
            </h2>
            <p className="text-xl text-gray-300 text-center mb-12 max-w-3xl mx-auto">
              Sure, you could use 3 separate apps (Widget Crew for pay, Flight Crew View for schedule, Flighty for tracking)...
              <br /><br />
              <span className="text-[#4A90E2] font-semibold">Or you could use Winglet and have it all in one place, with better pay protection than anyone else offers.</span>
            </p>
            <div className="flex justify-center">
              <Link 
                to="/compare-tools" 
                className="bg-white hover:bg-gray-100 text-[#1A2B4A] px-8 py-4 rounded-lg text-lg font-semibold transition-all inline-flex items-center"
              >
                See Full Comparison
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Highlights */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#0F1824]">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-heading font-bold text-white text-center mb-4">
            Built by Pilots, For Pilots
          </h2>
          <p className="text-xl text-gray-400 text-center mb-16">
            Every feature designed to solve real problems you face every day.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <FeatureCard 
              icon={<TrendingUp />}
              title="Automated Pay Protection"
              description="Catches company errors automatically. Line-by-line breakdown with PWA rule citations."
            />
            <FeatureCard 
              icon={<Shield />}
              title="Reroute Engine"
              description="RED/YELLOW/GREEN alerts. Section 23L calculator. VAS detection. Refusal letters."
            />
            <FeatureCard 
              icon={<Clock />}
              title="FAR 117 Predictive"
              description="Duty period analysis before you accept. Rolling limits tracker. Rest validation."
            />
            <FeatureCard 
              icon={<Plane />}
              title="Flight Tracking"
              description="Real-time OOOI updates. Gate changes. Delay alerts. Weather integration."
            />
            <FeatureCard 
              icon={<CheckCircle2 />}
              title="Timecard Audit"
              description="Company vs Winglet comparison. Variance detection. Grievance documentation."
            />
            <FeatureCard 
              icon={<Shield />}
              title="Calendar Sync"
              description="Export to Google/Apple. Rotation sync. Spillover allocation. Bid tracking."
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-heading font-bold text-white mb-6">
            Ready to Stop Losing Money?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Join hundreds of pilots protecting their pay with Winglet.
          </p>
          <Link 
            to="/signup" 
            className="bg-[#4A90E2] hover:bg-[#3A7BC8] text-white px-12 py-5 rounded-lg text-xl font-semibold transition-all transform hover:scale-105 inline-flex items-center group"
          >
            Start Free for 1.5 Months
            <ArrowRight className="ml-2 h-6 w-6 group-hover:translate-x-1 transition-transform" />
          </Link>
          <p className="text-gray-400 mt-4">
            No credit card required · Full feature access · Cancel anytime
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-800 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <Plane className="h-6 w-6 text-[#4A90E2]" />
                <span className="text-xl font-heading font-bold text-white">Winglet</span>
              </div>
              <p className="text-gray-400 text-sm">
                The AI-powered co-pilot for your career.
              </p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/how-it-works" className="text-gray-400 hover:text-[#4A90E2]">How It Works</Link></li>
                <li><Link to="/pricing" className="text-gray-400 hover:text-[#4A90E2]">Pricing</Link></li>
                <li><Link to="/compare-tools" className="text-gray-400 hover:text-[#4A90E2]">Compare</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/about" className="text-gray-400 hover:text-[#4A90E2]">About Us</Link></li>
                <li><Link to="/blog" className="text-gray-400 hover:text-[#4A90E2]">Blog</Link></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-[#4A90E2]">Contact</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/privacy" className="text-gray-400 hover:text-[#4A90E2]">Privacy Policy</Link></li>
                <li><Link to="/terms" className="text-gray-400 hover:text-[#4A90E2]">Terms of Service</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-gray-400 text-sm">
            <p>© 2026 Winglet. Built by pilots, for pilots. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

const FeatureCard = ({ icon, title, description }) => (
  <div className="bg-[#1A2B4A] border border-[#4A90E2]/20 rounded-xl p-6 hover:border-[#4A90E2]/40 transition-all group">
    <div className="w-12 h-12 bg-[#4A90E2]/20 rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#4A90E2]/30 transition-colors">
      {React.cloneElement(icon, { className: "h-6 w-6 text-[#4A90E2]" })}
    </div>
    <h3 className="text-lg font-heading font-bold text-white mb-2">{title}</h3>
    <p className="text-gray-400 text-sm">{description}</p>
  </div>
);

export default LandingPage;
